// Payroll Month Status Utility Functions

import { 
  PayrollPeriod, 
  MonthStatus, 
  MonthStatusType, 
  PayrollStatus, 
  PayrollAction,
  PayrollHistoryEntry,
  MonthNavigationState 
} from '@/types/payroll-periods';

// User-friendly month state system (simplified from complex month types)
export type UserMonthState = 'FUTURE' | 'UPCOMING' | 'IN_PROGRESS' | 'TO_BE_PROCESSED' | 'PROCESSED';

export interface UserFriendlyMonthStatus extends MonthStatus {
  userState: UserMonthState;
  userStateLabel: string;
  userStateDescription: string;
  primaryThemeColor: string;
  secondaryThemeColor: string;
  iconName: string;
  relevantDataSources: string[];
  contextualAlerts: string[];
}
import { format, startOfMonth, endOfMonth, isThisMonth, isFuture, isPast, addMonths, subMonths } from 'date-fns';

// Custom implementation since isLastMonth is not exported by date-fns
const isLastMonth = (date: Date) => {
  const now = new Date();
  const lastMonth = subMonths(now, 1);
  return date.getMonth() === lastMonth.getMonth() && date.getFullYear() === lastMonth.getFullYear();
};

/**
 * Determines the status type of a given month
 */
export function getMonthStatusType(period: PayrollPeriod, isPlanning?: boolean): MonthStatusType {
  const periodDate = new Date(period.year, period.month - 1, 1);
  const now = new Date();
  
  if (isThisMonth(periodDate)) {
    return isPlanning ? 'current_planning' : 'current_incomplete';
  } else if (isLastMonth(periodDate)) {
    return 'last_complete';
  } else if (isFuture(periodDate)) {
    return isPlanning ? 'future_planning' : 'future';
  } else if (isPast(periodDate)) {
    return 'historical';
  }
  
  return 'historical';
}

/**
 * Determines the status type with planning data awareness
 */
export function getMonthStatusTypeWithPlanningData(
  period: PayrollPeriod, 
  hasScheduledShifts: boolean,
  hasActualData: boolean
): MonthStatusType {
  const periodDate = new Date(period.year, period.month - 1, 1);
  const now = new Date();
  
  if (isThisMonth(periodDate)) {
    // Current month: prefer actual data if available, otherwise use planning
    if (hasActualData) {
      return 'current_incomplete';
    } else if (hasScheduledShifts) {
      return 'current_planning';
    } else {
      return 'current_incomplete';
    }
  } else if (isLastMonth(periodDate)) {
    return 'last_complete';
  } else if (isFuture(periodDate)) {
    return hasScheduledShifts ? 'future_planning' : 'future';
  } else if (isPast(periodDate)) {
    return 'historical';
  }
  
  return 'historical';
}

/**
 * Creates a PayrollPeriod object from a Date
 */
export function createPayrollPeriod(date: Date): PayrollPeriod {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    date: new Date(date.getFullYear(), date.getMonth(), 1),
    displayName: format(date, 'MMMM yyyy')
  };
}

/**
 * Gets available actions for a month based on its status
 */
export function getAvailableActions(monthStatus: MonthStatus): PayrollAction[] {
  const actions: PayrollAction[] = [];
  
  switch (monthStatus.type) {
    case 'current_incomplete':
      actions.push({
        id: 'calculate',
        label: 'Calculate Payroll',
        icon: 'Calculator',
        variant: 'default',
        requiresConfirmation: true,
        confirmationMessage: 'This month is still in progress. Calculate preliminary payroll?',
        disabled: !monthStatus.canCalculate,
        disabledReason: monthStatus.canCalculate ? undefined : 'Insufficient data for calculation'
      });
      
      actions.push({
        id: 'view_progress',
        label: 'View Progress',
        icon: 'Clock',
        variant: 'outline',
        requiresConfirmation: false,
        disabled: false
      });
      
      if (monthStatus.employeesWithPayroll > 0) {
        actions.push({
          id: 'export_draft',
          label: 'Export Draft',
          icon: 'Download',
          variant: 'outline',
          requiresConfirmation: true,
          confirmationMessage: 'Export preliminary payroll data?',
          disabled: false
        });
      }
      break;
      
    case 'last_complete':
      if (monthStatus.status === 'draft' || monthStatus.status === 'calculated') {
        actions.push({
          id: 'finalize',
          label: 'Finalize Payroll',
          icon: 'CheckCircle',
          variant: 'default',
          requiresConfirmation: true,
          confirmationMessage: 'Finalize payroll for this month? This action cannot be undone.',
          disabled: !monthStatus.canFinalize,
          disabledReason: monthStatus.canFinalize ? undefined : 'Requirements not met for finalization'
        });
      }
      
      actions.push({
        id: 'recalculate',
        label: 'Recalculate',
        icon: 'RefreshCw',
        variant: 'outline',
        requiresConfirmation: true,
        confirmationMessage: 'Recalculate payroll? This will override existing calculations.',
        disabled: monthStatus.status === 'locked' || monthStatus.status === 'paid',
        disabledReason: monthStatus.status === 'locked' ? 'Payroll is locked' : monthStatus.status === 'paid' ? 'Payroll already paid' : undefined
      });
      
      actions.push({
        id: 'export',
        label: 'Export All',
        icon: 'Download',
        variant: 'outline',
        requiresConfirmation: false,
        disabled: false
      });
      
      if (monthStatus.status === 'finalized') {
        actions.push({
          id: 'process_payments',
          label: 'Process Payments',
          icon: 'CreditCard',
          variant: 'default',
          requiresConfirmation: true,
          confirmationMessage: 'Process payments for all employees? This will initiate bank transfers.',
          disabled: false
        });
      }
      break;
      
    case 'historical':
      actions.push({
        id: 'view_only',
        label: 'View Details',
        icon: 'Eye',
        variant: 'outline',
        requiresConfirmation: false,
        disabled: false
      });
      
      actions.push({
        id: 'export_historical',
        label: 'Export',
        icon: 'Download',
        variant: 'outline',
        requiresConfirmation: false,
        disabled: false
      });
      
      if (monthStatus.status !== 'locked') {
        actions.push({
          id: 'archive',
          label: 'Archive',
          icon: 'Archive',
          variant: 'outline',
          requiresConfirmation: true,
          confirmationMessage: 'Archive this payroll period? Archived periods are read-only.',
          disabled: false
        });
      }
      break;
      
    case 'future':
      actions.push({
        id: 'plan',
        label: 'Plan & Forecast',
        icon: 'Calendar',
        variant: 'outline',
        requiresConfirmation: false,
        disabled: false
      });
      
      actions.push({
        id: 'apply_template',
        label: 'Apply Template',
        icon: 'FileTemplate',
        variant: 'outline',
        requiresConfirmation: true,
        confirmationMessage: 'Apply payroll template to this future period?',
        disabled: false
      });
      break;
      
    case 'locked':
      actions.push({
        id: 'view_locked',
        label: 'View (Locked)',
        icon: 'Lock',
        variant: 'outline',
        requiresConfirmation: false,
        disabled: false
      });
      break;
  }
  
  return actions;
}

/**
 * Generates warnings for a month status
 */
export function getMonthStatusWarnings(monthStatus: MonthStatus): string[] {
  const warnings: string[] = [];
  
  switch (monthStatus.type) {
    case 'current_incomplete': {
      if (monthStatus.completionPercentage < 50) {
        warnings.push('Less than 50% of employees have logged time this month');
      }
      
      if (monthStatus.employeesWithPayroll === 0) {
        warnings.push('No payroll records found - employees may not have logged time yet');
      }
      
      const daysLeftInMonth = new Date(monthStatus.period.year, monthStatus.period.month, 0).getDate() - new Date().getDate();
      if (daysLeftInMonth <= 3) {
        warnings.push(`Only ${daysLeftInMonth} days left in the month`);
      }
      break;
    }
      
    case 'last_complete':
      if (monthStatus.status === 'draft' && new Date().getDate() > 5) {
        warnings.push('Payroll should typically be finalized by the 5th of the following month');
      }
      
      if (monthStatus.employeesWithPayroll < monthStatus.employeeCount) {
        const missingCount = monthStatus.employeeCount - monthStatus.employeesWithPayroll;
        warnings.push(`${missingCount} employee(s) missing payroll data`);
      }
      break;
      
    case 'historical':
      if (monthStatus.status === 'draft') {
        warnings.push('Historical payroll period is still in draft status');
      }
      break;
      
    case 'future':
      if (monthStatus.employeesWithPayroll > 0) {
        warnings.push('Future period has payroll data - this may be an error');
      }
      break;
  }
  
  return warnings;
}

/**
 * Calculates completion percentage for a month
 */
export function calculateCompletionPercentage(
  employeeCount: number,
  employeesWithPayroll: number,
  monthType: MonthStatusType
): number {
  if (employeeCount === 0) return 0;
  
  const basePercentage = (employeesWithPayroll / employeeCount) * 100;
  
  // Adjust based on month type
  switch (monthType) {
    case 'current_incomplete':
      // Current month completion is based on employee time logging
      return Math.min(basePercentage, 95); // Never 100% for current month
      
    case 'last_complete':
      return basePercentage;
      
    case 'historical':
      return basePercentage;
      
    case 'future':
      return 0; // Future months have no completion
      
    default:
      return basePercentage;
  }
}

/**
 * Creates a month status object with all calculated properties
 */
export function createMonthStatus(
  period: PayrollPeriod,
  payrollData: {
    status: PayrollStatus;
    employeeCount: number;
    employeesWithPayroll: number;
    totalGrossPay: number;
    totalNetPay: number;
    lastModified?: Date;
    approvedBy?: string;
    approvedAt?: Date;
    lockedAt?: Date;
    paidAt?: Date;
  }
): MonthStatus {
  const type = getMonthStatusType(period);
  const completionPercentage = calculateCompletionPercentage(
    payrollData.employeeCount,
    payrollData.employeesWithPayroll,
    type
  );
  
  const monthStatus: MonthStatus = {
    period,
    type,
    status: payrollData.status,
    completionPercentage,
    employeeCount: payrollData.employeeCount,
    employeesWithPayroll: payrollData.employeesWithPayroll,
    totalGrossPay: payrollData.totalGrossPay,
    totalNetPay: payrollData.totalNetPay,
    canEdit: type !== 'historical' && type !== 'locked' && payrollData.status !== 'locked' && payrollData.status !== 'paid',
    canCalculate: type !== 'future' && payrollData.status !== 'locked' && payrollData.status !== 'paid',
    canFinalize: type === 'last_complete' && (payrollData.status === 'calculated' || payrollData.status === 'draft'),
    canExport: true, // Always allow export
    lastModified: payrollData.lastModified,
    approvedBy: payrollData.approvedBy,
    approvedAt: payrollData.approvedAt,
    lockedAt: payrollData.lockedAt,
    paidAt: payrollData.paidAt,
    warnings: [],
    actions: []
  };
  
  monthStatus.warnings = getMonthStatusWarnings(monthStatus);
  monthStatus.actions = getAvailableActions(monthStatus);
  
  return monthStatus;
}

/**
 * Gets the status badge variant and color for UI display
 */
export function getStatusBadgeProps(monthStatus: MonthStatus): {
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  color: string;
  label: string;
} {
  switch (monthStatus.type) {
    case 'current_incomplete':
      return {
        variant: 'default',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800',
        label: 'Current (In Progress)'
      };

    case 'last_complete':
      if (monthStatus.status === 'paid') {
        return {
          variant: 'default',
          color: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
          label: 'Complete'
        };
      } else {
        return {
          variant: 'outline',
          color: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
          label: 'Ready to Process'
        };
      }

    case 'historical':
      return {
        variant: 'outline',
        color: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700',
        label: 'Historical'
      };

    case 'current_planning':
      return {
        variant: 'default',
        color: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
        label: 'Current (Planned)'
      };

    case 'future_planning':
      return {
        variant: 'outline',
        color: 'bg-primary/10 text-primary border-primary/20',
        label: 'Planned'
      };

    case 'future':
      return {
        variant: 'outline',
        color: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
        label: 'Future'
      };

    case 'locked':
      return {
        variant: 'destructive',
        color: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
        label: 'Locked'
      };

    default:
      return {
        variant: 'outline',
        color: 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700',
        label: 'Unknown'
      };
  }
}

/**
 * Generates a range of available periods for selection
 */
export function generateAvailablePeriods(
  currentDate: Date = new Date(),
  monthsBack: number = 12,
  monthsForward: number = 3
): PayrollPeriod[] {
  const periods: PayrollPeriod[] = [];
  
  // Add historical months
  for (let i = monthsBack; i > 0; i--) {
    const date = subMonths(currentDate, i);
    periods.push(createPayrollPeriod(date));
  }
  
  // Add current month
  periods.push(createPayrollPeriod(currentDate));
  
  // Add future months
  for (let i = 1; i <= monthsForward; i++) {
    const date = addMonths(currentDate, i);
    periods.push(createPayrollPeriod(date));
  }
  
  return periods;
}

/**
 * Creates navigation state for month selector
 */
export function createMonthNavigationState(
  currentPeriod: PayrollPeriod,
  recentlyViewed: PayrollHistoryEntry[] = [],
  favorites: PayrollPeriod[] = []
): MonthNavigationState {
  return {
    currentPeriod,
    availablePeriods: generateAvailablePeriods(),
    recentlyViewed: recentlyViewed.slice(0, 5), // Limit to 5 recent
    favorites: favorites.slice(0, 3), // Limit to 3 favorites
    quickActions: [
      {
        id: 'current_month',
        label: 'Current Month',
        icon: 'Calendar',
        variant: 'default',
        requiresConfirmation: false,
        disabled: false
      },
      {
        id: 'last_month',
        label: 'Last Month',
        icon: 'ChevronLeft',
        variant: 'outline',
        requiresConfirmation: false,
        disabled: false
      },
      {
        id: 'export_multiple',
        label: 'Bulk Export',
        icon: 'Download',
        variant: 'outline',
        requiresConfirmation: true,
        confirmationMessage: 'Export multiple months?',
        disabled: false
      }
    ]
  };
}

/**
 * Creates a planning-based month status using scheduled shift data
 */
export async function createPlanningMonthStatus(
  period: PayrollPeriod,
  businessId: string
): Promise<MonthStatus> {
  const { getBusinessPlanningSummary } = await import('./planning/shift-planning-utils');
  
  try {
    const planningSummary = await getBusinessPlanningSummary(businessId, period.year, period.month);
    const type = getMonthStatusType(period, true);
    
    const monthStatus: MonthStatus = {
      period,
      type,
      status: 'draft', // Planning is always draft status
      completionPercentage: planningSummary.employeesWithShifts > 0 ? 
        Math.min((planningSummary.employeesWithShifts / planningSummary.totalEmployees) * 100, 95) : 0,
      employeeCount: planningSummary.totalEmployees,
      employeesWithPayroll: planningSummary.employeesWithShifts,
      totalGrossPay: planningSummary.totalProjectedGrossPay,
      totalNetPay: planningSummary.totalProjectedGrossPay * 0.75, // Estimate net as 75% of gross
      canEdit: true,
      canCalculate: planningSummary.employeesWithShifts > 0,
      canFinalize: false, // Cannot finalize planning data
      canExport: planningSummary.employeesWithShifts > 0,
      warnings: [],
      actions: [],
      
      // Planning-specific data
      isPlanning: true,
      totalPlannedHours: planningSummary.totalPlannedHours,
      employeesWithScheduledShifts: planningSummary.employeesWithShifts,
      overScheduledEmployees: planningSummary.overScheduledEmployees,
      underScheduledEmployees: planningSummary.underScheduledEmployees,
      projectedGrossPay: planningSummary.totalProjectedGrossPay,
      planningComplianceIssues: planningSummary.complianceIssues.map(issue => ({
        employee_name: issue.employee_name,
        compliance_status: issue.warnings.some(w => w.includes('Over-scheduled')) ? 'over_scheduled' :
                          issue.warnings.some(w => w.includes('Under-scheduled')) ? 'under_scheduled' : 'compliant',
        variance_hours: 0, // Would need to calculate this from warnings
        warnings: issue.warnings
      }))
    };
    
    monthStatus.warnings = getPlanningStatusWarnings(monthStatus);
    monthStatus.actions = getPlanningAvailableActions(monthStatus);
    
    return monthStatus;
  } catch (error) {
    console.error('Error creating planning month status:', error);
    
    // Return empty planning status on error
    return {
      period,
      type: getMonthStatusType(period, true),
      status: 'draft',
      completionPercentage: 0,
      employeeCount: 0,
      employeesWithPayroll: 0,
      totalGrossPay: 0,
      totalNetPay: 0,
      canEdit: true,
      canCalculate: false,
      canFinalize: false,
      canExport: false,
      warnings: ['Unable to load planning data'],
      actions: [],
      isPlanning: true
    };
  }
}

/**
 * Get warnings specific to planning status
 */
function getPlanningStatusWarnings(monthStatus: MonthStatus): string[] {
  const warnings: string[] = [];
  
  if (!monthStatus.isPlanning) return warnings;
  
  if ((monthStatus.overScheduledEmployees || 0) > 0) {
    warnings.push(`${monthStatus.overScheduledEmployees} employee(s) are over-scheduled and will incur overtime costs`);
  }
  
  if ((monthStatus.underScheduledEmployees || 0) > 0) {
    warnings.push(`${monthStatus.underScheduledEmployees} salary employee(s) are under-scheduled but will receive full pay`);
  }
  
  if ((monthStatus.employeesWithScheduledShifts || 0) === 0) {
    warnings.push('No shifts have been scheduled for this period');
  }
  
  const scheduledPercent = monthStatus.employeeCount > 0 ? 
    ((monthStatus.employeesWithScheduledShifts || 0) / monthStatus.employeeCount) * 100 : 0;
  
  if (scheduledPercent < 50) {
    warnings.push(`Only ${scheduledPercent.toFixed(0)}% of employees have scheduled shifts`);
  }
  
  return warnings;
}

/**
 * Get available actions for planning status
 */
function getPlanningAvailableActions(monthStatus: MonthStatus): PayrollAction[] {
  const actions: PayrollAction[] = [];
  
  if (!monthStatus.isPlanning) return actions;
  
  // View/Edit Schedule action
  actions.push({
    id: 'view_schedule',
    label: 'View Schedule',
    icon: 'Calendar',
    variant: 'outline',
    requiresConfirmation: false,
    disabled: false
  });
  
  // Calculate projected payroll
  if (monthStatus.canCalculate) {
    actions.push({
      id: 'calculate_projection',
      label: 'Calculate Projection',
      icon: 'Calculator',
      variant: 'default',
      requiresConfirmation: false,
      disabled: false
    });
  }
  
  // Export planning data
  if (monthStatus.canExport) {
    actions.push({
      id: 'export_planning',
      label: 'Export Planning',
      icon: 'Download',
      variant: 'outline',
      requiresConfirmation: false,
      disabled: false
    });
  }
  
  // Address compliance issues
  if ((monthStatus.planningComplianceIssues?.length || 0) > 0) {
    actions.push({
      id: 'fix_compliance',
      label: 'Fix Compliance Issues',
      icon: 'AlertTriangle',
      variant: 'outline',
      requiresConfirmation: false,
      disabled: false
    });
  }
  
  return actions;
}

/**
 * Maps complex month types to user-friendly states and calculates months from now
 */
function getMonthsFromNow(period: PayrollPeriod): number {
  const now = new Date();
  const periodDate = new Date(period.year, period.month - 1, 1);
  const monthsDiff = (periodDate.getFullYear() - now.getFullYear()) * 12 + (periodDate.getMonth() - now.getMonth());
  return monthsDiff;
}

/**
 * Converts complex month status to user-friendly state system
 */
export function getUserFriendlyMonthState(
  monthStatus: MonthStatus,
  payrollStatus?: PayrollStatus
): UserMonthState {
  const monthsFromNow = getMonthsFromNow(monthStatus.period);
  const status = payrollStatus || monthStatus.status;

  // PROCESSED/HISTORICAL (completed months)
  if (monthStatus.type === 'historical' || status === 'paid' || status === 'locked') {
    return 'PROCESSED';
  }

  // TO BE PROCESSED (last month)
  if (monthStatus.type === 'last_complete') {
    return 'TO_BE_PROCESSED';
  }

  // IN PROGRESS (current month)
  if (monthStatus.type === 'current_incomplete' || monthStatus.type === 'current_planning') {
    return 'IN_PROGRESS';
  }

  // UPCOMING (next month)
  if (monthsFromNow === 1) {
    return 'UPCOMING';
  }

  // FUTURE (2+ months ahead)
  if (monthsFromNow >= 2) {
    return 'FUTURE';
  }

  // Default fallback based on time relationship
  if (monthsFromNow > 0) return 'FUTURE';
  if (monthsFromNow === 0) return 'IN_PROGRESS';
  return 'PROCESSED';
}

/**
 * Gets state-specific configuration for UI display
 */
export function getUserFriendlyStateConfig(userState: UserMonthState): {
  label: string;
  description: string;
  primaryColor: string;
  secondaryColor: string;
  iconName: string;
  relevantDataSources: string[];
} {
  switch (userState) {
    case 'FUTURE':
      return {
        label: 'Future',
        description: 'Planning & forecasting for upcoming periods',
        primaryColor: 'text-primary',
        secondaryColor: 'bg-primary/10 border-primary/20',
        iconName: 'TrendingUp',
        relevantDataSources: ['planning', 'templates', 'budgets']
      };

    case 'UPCOMING':
      return {
        label: 'Upcoming',
        description: 'Preparation for next month',
        primaryColor: 'text-blue-600 dark:text-blue-400',
        secondaryColor: 'bg-blue-100 border-blue-200 dark:bg-blue-900/30 dark:border-blue-800',
        iconName: 'Calendar',
        relevantDataSources: ['scheduling', 'templates', 'notifications']
      };

    case 'IN_PROGRESS':
      return {
        label: 'In Progress',
        description: 'Current month - active tracking',
        primaryColor: 'text-green-600 dark:text-green-400',
        secondaryColor: 'bg-green-100 border-green-200 dark:bg-green-900/30 dark:border-green-800',
        iconName: 'Activity',
        relevantDataSources: ['timeLogs', 'liveData', 'scheduling']
      };

    case 'TO_BE_PROCESSED':
      return {
        label: 'To Be Processed',
        description: 'Ready for payment processing',
        primaryColor: 'text-orange-600 dark:text-orange-400',
        secondaryColor: 'bg-orange-100 border-orange-200 dark:bg-orange-900/30 dark:border-orange-800',
        iconName: 'CreditCard',
        relevantDataSources: ['payroll', 'approvals', 'compliance']
      };

    case 'PROCESSED':
      return {
        label: 'Processed',
        description: 'Completed and archived',
        primaryColor: 'text-gray-600 dark:text-gray-400',
        secondaryColor: 'bg-gray-100 border-gray-200 dark:bg-gray-800/50 dark:border-gray-700',
        iconName: 'Archive',
        relevantDataSources: ['archived', 'historical', 'audit']
      };

    default:
      return {
        label: 'Unknown',
        description: 'Unknown state',
        primaryColor: 'text-gray-500 dark:text-gray-400',
        secondaryColor: 'bg-gray-50 border-gray-200 dark:bg-gray-800/30 dark:border-gray-700',
        iconName: 'HelpCircle',
        relevantDataSources: []
      };
  }
}

/**
 * Creates enhanced month status with user-friendly state
 */
export function createUserFriendlyMonthStatus(
  monthStatus: MonthStatus,
  contextualAlerts: string[] = []
): UserFriendlyMonthStatus {
  const userState = getUserFriendlyMonthState(monthStatus);
  const stateConfig = getUserFriendlyStateConfig(userState);
  
  return {
    ...monthStatus,
    userState,
    userStateLabel: stateConfig.label,
    userStateDescription: stateConfig.description,
    primaryThemeColor: stateConfig.primaryColor,
    secondaryThemeColor: stateConfig.secondaryColor,
    iconName: stateConfig.iconName,
    relevantDataSources: stateConfig.relevantDataSources,
    contextualAlerts
  };
}